/**
 * MSAL Auth Provider Component
 * Phase 6: Frontend Application
 *
 * Wraps the application with MSAL authentication context.
 * Handles:
 * - MSAL instance initialisation
 * - Authentication state management
 * - Account selection
 * - Redirect callback handling
 */

'use client';

import { EventType, PublicClientApplication, type AuthenticationResult, type EventMessage } from '@azure/msal-browser';
import { MsalProvider as BaseMsalProvider } from '@azure/msal-react';
import { useEffect, useState, type ReactNode } from 'react';
import { logger } from '../logger';
import { isAuthConfigured, msalConfig } from './msal-config';

// Create MSAL instance (singleton)
let msalInstance: PublicClientApplication | null = null;
let msalInitPromise: Promise<void> | null = null;

function getMsalInstance(): PublicClientApplication | null {
  if (!isAuthConfigured()) {
    logger.warn('Auth not configured - MSAL disabled');
    return null;
  }

  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);

    // Set up event callbacks
    msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        msalInstance?.setActiveAccount(account);
        logger.debug('Login success - account set:', account?.username);
      }
    });
  }

  return msalInstance;
}

/**
 * Initialise MSAL and handle any redirect response
 */
async function initializeMsal(instance: PublicClientApplication): Promise<void> {
  if (msalInitPromise) {
    return msalInitPromise;
  }

  msalInitPromise = (async () => {
    try {
      // Initialize MSAL
      await instance.initialize();
      
      // Handle redirect response (this is critical after login redirect)
      const response = await instance.handleRedirectPromise();
      
      if (response) {
        // User just logged in via redirect
        logger.debug('Redirect login completed for:', response.account?.username);
        instance.setActiveAccount(response.account);
      } else {
        // No redirect response - check for existing accounts
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0 && accounts[0]) {
          instance.setActiveAccount(accounts[0]);
          logger.debug('Existing account found:', accounts[0].username);
        }
      }
    } catch (error) {
      logger.error('MSAL initialisation error:', error);
    }
  })();

  return msalInitPromise;
}

interface MsalProviderProps {
  children: ReactNode;
}

/**
 * MSAL Authentication Provider
 * Wraps children with MSAL context if auth is configured
 */
export function MsalAuthProvider({ children }: MsalProviderProps): JSX.Element {
  const [instance, setInstance] = useState<PublicClientApplication | null>(null);
  const [isInitialised, setIsInitialised] = useState(false);

  useEffect(() => {
    const msalInst = getMsalInstance();
    
    if (msalInst) {
      // Initialise MSAL and handle redirect
      initializeMsal(msalInst).then(() => {
        setInstance(msalInst);
        setIsInitialised(true);
      });
    } else {
      setIsInitialised(true);
    }
  }, []);

  // Show loading state while MSAL initialises
  if (!isInitialised) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent mx-auto" />
          <p className="text-gray-600">Initialising authentication...</p>
        </div>
      </div>
    );
  }

  // If auth not configured, render children without MSAL
  if (!instance) {
    return <>{children}</>;
  }

  return (
    <BaseMsalProvider instance={instance}>
      {children}
    </BaseMsalProvider>
  );
}

/**
 * Export the MSAL instance for use in API calls
 */
export function getMsalInstanceForApi(): PublicClientApplication | null {
  return msalInstance;
}
